import React, { useMemo, useState } from "react";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { getErrorMessage } from "utils/errors";
import type { AdminManualResultIn } from "api/adminResults";
import MarkerRowEditor from "./MarkerRowEditor";

type Sex = "Female" | "Male" | "Other" | "Unknown" | "";

type MarkerState = {
  code: string;
  name: string;
  value: string;
  unit: string;
  ref_low: string;
  ref_high: string;
  flag: string;
  notes: string;
};

type PatientState = {
  first_name: string;
  last_name: string;
  date_of_birth: string; // normalized to ISO date
  sex: Sex;
  collected_at: string; // normalized to ISO datetime
  report_date: string; // optional; leave as-is for now
  lab_name: string;
  lab_reference: string;
};

export type StructuredResult = {
  schema_version: "1.0";
  patient: any;
  markers: any;
  notes?: string | null;
};

export interface ManualResultsFormProps {
  initialData?: any;
  initialTitle?: string | null;
  initialSummary?: string | null;
  initialGeneratePdf?: boolean;

  submitLabel?: string;
  onSubmit: (payload: AdminManualResultIn) => Promise<void>;
  onCancel?: () => void;
}

type MarkerTemplateItem = {
  code: string;
  name: string;
  unit?: string;
};

/**
 * Prefill template: canonical markers admins commonly enter.
 * Marker identity (name/unit/code) is fixed; admins fill only values/ranges/notes.
 */
const MARKER_TEMPLATE: MarkerTemplateItem[] = [
  // Hormones (Big Four)
  { code: "E2", name: "Oestradiol (E2)", unit: "pmol/L" },
  { code: "FSH", name: "Follicle Stimulating Hormone (FSH)", unit: "mIU/mL" },
  { code: "LH", name: "Luteinising Hormone (LH)", unit: "mIU/mL" },
  { code: "PROG", name: "Progesterone", unit: "nmol/L" },

  // Androgens
  { code: "TESTO", name: "Testosterone", unit: "nmol/L" },
  { code: "SHBG", name: "Sex Hormone Binding Globulin (SHBG)", unit: "nmol/L" },
  { code: "FAI", name: "Free Androgen Index (FAI)", unit: "index" },

  // Thyroid
  { code: "TSH", name: "TSH", unit: "mIU/L" },
  { code: "FT4", name: "Free T4 (Thyroxine)", unit: "pmol/L" },
  { code: "FT3", name: "Free T3 (Triiodothyronine)", unit: "pmol/L" },

  // Thyroid antibodies
  { code: "ANTI_TPO", name: "Anti-TPO", unit: "U/mL" },
  { code: "ANTI_TG", name: "Anti-TG", unit: "U/mL" },

  // Other hormones
  { code: "PROLACTIN", name: "Prolactin", unit: "µIU/mL" },

  // Bone
  { code: "ALP", name: "Alkaline Phosphatase (ALP)", unit: "IU/L" },
  { code: "CALCIUM_CORR", name: "Calcium (Corrected)", unit: "mmol/L" },
  { code: "PHOSPHATE", name: "Phosphate", unit: "mmol/L" },
  { code: "PTH", name: "Parathyroid Hormone (PTH)", unit: "pmol/L" },
  { code: "VITD_25OH", name: "Vitamin D (25-OH)", unit: "nmol/L" },

  // Metabolic / lipids
  { code: "CHOL_TOTAL", name: "Total Cholesterol", unit: "mmol/L" },
  { code: "CHOL_LDL", name: "LDL Cholesterol", unit: "mmol/L" },
  { code: "CHOL_HDL", name: "HDL Cholesterol", unit: "mmol/L" },
  { code: "CHOL_RATIO", name: "Cholesterol Ratio", unit: "ratio" },
  { code: "TRIG", name: "Triglycerides", unit: "mmol/L" },
  { code: "CRP_HS", name: "C-Reactive Protein (hs-CRP)", unit: "mg/L" },
  { code: "HBA1C", name: "HbA1c", unit: "% / mmol/mol" },

  // Stress
  { code: "CORTISOL", name: "Cortisol", unit: "nmol/L" },
  { code: "DHEA_S", name: "DHEA-S", unit: "µmol/L" },

  // Nutrition / iron status
  { code: "IRON_SERUM", name: "Serum Iron", unit: "µmol/L" },
  { code: "FERRITIN", name: "Ferritin", unit: "ng/mL" },
  { code: "TIBC", name: "TIBC", unit: "µmol/L" },
  { code: "TRANSFERRIN", name: "Transferrin", unit: "g/L" },
  { code: "TRANSF_SAT", name: "Transferrin Saturation", unit: "%" },

  // Nutrition / other
  { code: "ALBUMIN", name: "Albumin", unit: "g/L" },
  { code: "MAGNESIUM", name: "Magnesium", unit: "mmol/L" },
  { code: "FOLATE_B9", name: "Folate (B9)", unit: "ng/mL" },
  { code: "B12", name: "Vitamin B12", unit: "pg/mL" },
  { code: "ZINC", name: "Zinc", unit: "µmol/L" },

  // Full Blood Count (common individual markers)
  { code: "HAEMOGLOBIN", name: "Haemoglobin (Hb)", unit: "g/L" },
  { code: "RBC", name: "Red Blood Cell Count (RBC)", unit: "x10¹²/L" },
  { code: "HCT", name: "Haematocrit (Hct)", unit: "L/L" },
  { code: "MCV", name: "Mean Corpuscular Volume (MCV)", unit: "fL" },
  { code: "MCH", name: "Mean Corpuscular Haemoglobin (MCH)", unit: "pg" },
  { code: "MCHC", name: "Mean Corpuscular Haemoglobin Concentration (MCHC)", unit: "g/L" },
  { code: "RDW", name: "Red Cell Distribution Width (RDW)", unit: "%" },
  { code: "PLATELETS", name: "Platelets", unit: "x10⁹/L" },
  { code: "MPV", name: "Mean Platelet Volume (MPV)", unit: "fL" },
  { code: "WBC", name: "White Blood Cell Count (WBC)", unit: "x10⁹/L" },
  { code: "NEUTROPHILS", name: "Neutrophils", unit: "x10⁹/L" },
  { code: "LYMPHOCYTES", name: "Lymphocytes", unit: "x10⁹/L" },
  { code: "MONOCYTES", name: "Monocytes", unit: "x10⁹/L" },
  { code: "EOSINOPHILS", name: "Eosinophils", unit: "x10⁹/L" },
  { code: "BASOPHILS", name: "Basophils", unit: "x10⁹/L" },
];

/**
 * Default reference ranges for the admin template.
 * These should match backend preset defaults.
 *
 * Note: threshold-only markers are represented as one-sided ranges:
 *  - "< x" => { low: null, high: x }
 *  - "> x" => { low: x, high: null }
 */
const DEFAULT_REF_RANGES_BY_CODE: Record<string, { low: number | null; high: number | null }> = {
  // Big Four (default follicular)
  E2: { low: 71.6, high: 529.2 },
  FSH: { low: 2.5, high: 10.2 },
  LH: { low: 1.9, high: 12.5 },
  PROG: { low: 0.0, high: 4.45 },

  // Androgens
  TESTO: { low: 0.5, high: 2.6 },
  SHBG: { low: 27.8, high: 146.0 },
  FAI: { low: 0.5, high: 6.5 },

  // Thyroid
  TSH: { low: 0.55, high: 4.78 },
  FT4: { low: 11.5, high: 22.7 },
  FT3: { low: 2.6, high: 7.1 },

  // Thyroid antibodies (threshold)
  ANTI_TPO: { low: null, high: 13.8 },
  ANTI_TG: { low: null, high: 4.4 },

  // Other hormones
  PROLACTIN: { low: 59.0, high: 619.0 },

  // Bone
  ALP: { low: 46.0, high: 116.0 },
  CALCIUM_CORR: { low: 2.2, high: 2.6 },
  PHOSPHATE: { low: 0.8, high: 1.5 },
  PTH: { low: 0.98, high: 4.73 },
  // Vitamin D: default is ">50" (categorical exists, but we keep a single numeric threshold in the form)
  VITD_25OH: { low: 50.0, high: null },

  // Lipids / metabolic
  CHOL_TOTAL: { low: null, high: 5.18 },
  CHOL_LDL: { low: 0.5, high: 3.0 },
  CHOL_HDL: { low: 1.55, high: null },
  CHOL_RATIO: { low: null, high: 4.5 },
  TRIG: { low: null, high: 1.7 },
  CRP_HS: { low: null, high: 5.0 },
  HBA1C: { low: null, high: 6.1 },

  // Stress
  CORTISOL: { low: 145.4, high: 619.4 },
  DHEA_S: { low: 0.26, high: 11.0 },

  // Nutrition / iron
  IRON_SERUM: { low: 9.0, high: 30.4 },
  FERRITIN: { low: 10.0, high: 291.0 },
  TIBC: { low: 44.0, high: 76.1 },
  TRANSFERRIN: { low: 2.5, high: 3.8 },
  TRANSF_SAT: { low: 18.0, high: 55.0 },

  // Nutrition / other
  ALBUMIN: { low: 34.0, high: 50.0 },
  MAGNESIUM: { low: 0.53, high: 1.11 },
  FOLATE_B9: { low: 3.0, high: 17.0 },
  B12: { low: 193.0, high: 982.0 },
  ZINC: { low: 10.7, high: 17.5 },

  // FBC
  HAEMOGLOBIN: { low: 120.0, high: 150.0 },
  RBC: { low: 3.8, high: 4.8 },
  HCT: { low: 0.36, high: 0.46 },
  MCV: { low: 83.0, high: 101.0 },
  MCH: { low: 27.0, high: 32.0 },
  MCHC: { low: 315.0, high: 345.0 },
  RDW: { low: 11.6, high: 14.0 },
  PLATELETS: { low: 150.0, high: 410.0 },
  MPV: { low: 7.0, high: 12.0 },
  WBC: { low: 4.0, high: 10.0 },
  NEUTROPHILS: { low: 2.0, high: 7.5 },
  LYMPHOCYTES: { low: 1.0, high: 3.5 },
  MONOCYTES: { low: 0.2, high: 1.0 },
  EOSINOPHILS: { low: 0.0, high: 0.4 },
  BASOPHILS: { low: 0.0, high: 0.1 },
};

function defaultRangeStrings(code: string): { ref_low: string; ref_high: string } {
  const d = DEFAULT_REF_RANGES_BY_CODE[code];
  if (!d) return { ref_low: "", ref_high: "" };
  return {
    ref_low: d.low == null ? "" : String(d.low),
    ref_high: d.high == null ? "" : String(d.high),
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function daysInMonth(y: number, m: number): number {
  if (m === 2) return isLeapYear(y) ? 29 : 28;
  if ([4, 6, 9, 11].includes(m)) return 30;
  return 31;
}

function isPlainObject(v: any): v is Record<string, any> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function normKey(s: string): string {
  return (s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function normVariant(s: string): string {
  // Keep underscores (common in extractor ids)
  return (s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_");
}

/**
 * Map extractor/renderer ids to the canonical template code.
 * This prevents data loss when editing extracted results.
 */
const VARIANT_TO_TEMPLATE_CODE: Record<string, string> = {
  // Hormones / thyroid / antibodies
  oestradiol: "E2",
  estradiol: "E2",
  fsh: "FSH",
  lh: "LH",
  progesterone: "PROG",
  testosterone_total: "TESTO",
  testosterone: "TESTO",
  shbg: "SHBG",
  fai: "FAI",
  tsh: "TSH",
  free_t4: "FT4",
  free_thyroxine: "FT4",
  free_t3: "FT3",
  free_triiodothyronine: "FT3",
  anti_tpo: "ANTI_TPO",
  tpo: "ANTI_TPO",
  anti_tg: "ANTI_TG",
  prolactin: "PROLACTIN",

  // Bone
  alkaline_phosphatase: "ALP",
  alp: "ALP",
  calcium_corrected: "CALCIUM_CORR",
  corrected_calcium: "CALCIUM_CORR",
  calcium_corr: "CALCIUM_CORR",
  calcium: "CALCIUM_CORR",
  phosphate: "PHOSPHATE",
  phosphorus: "PHOSPHATE",
  serum_phosphate: "PHOSPHATE",
  inorganic_phosphate: "PHOSPHATE",
  pth: "PTH",
  vitamin_d: "VITD_25OH",
  vitamin_d_25oh: "VITD_25OH",
  vitd: "VITD_25OH",
  vitd_25oh: "VITD_25OH",
  vit_d: "VITD_25OH",

  // Metabolic
  chol_total: "CHOL_TOTAL",
  total_cholesterol: "CHOL_TOTAL",
  ldl: "CHOL_LDL",
  hdl: "CHOL_HDL",
  tc_hdl_ratio: "CHOL_RATIO",
  chol_ratio: "CHOL_RATIO",
  triglycerides: "TRIG",
  trig: "TRIG",
  crp: "CRP_HS",
  hs_crp: "CRP_HS",
  c_reactive_protein: "CRP_HS",
  hba1c: "HBA1C",

  // Nutrition / iron status
  iron: "IRON_SERUM",
  serum_iron: "IRON_SERUM",
  ferritin: "FERRITIN",
  iron_binding_capacity: "TIBC",
  total_iron_binding_capacity: "TIBC",
  transferrin: "TRANSFERRIN",
  transferrin_saturation: "TRANSF_SAT",
  transf_sat: "TRANSF_SAT",

  // Nutrition / other
  albumin: "ALBUMIN",
  magnesium: "MAGNESIUM",
  folate: "FOLATE_B9",
  folic_acid: "FOLATE_B9",
  vitamin_b12: "B12",
  b12: "B12",
  zinc: "ZINC",

  // FBC
  haemoglobin: "HAEMOGLOBIN",
  hemoglobin: "HAEMOGLOBIN",
  hb: "HAEMOGLOBIN",
  hgb: "HAEMOGLOBIN",
  rbc: "RBC",
  hct: "HCT",
  mcv: "MCV",
  mch: "MCH",
  mchc: "MCHC",
  rdw: "RDW",
  platelets: "PLATELETS",
  mpv: "MPV",
  wbc: "WBC",
  wcc: "WBC",
  neutrophils: "NEUTROPHILS",
  lymphocytes: "LYMPHOCYTES",
  monocytes: "MONOCYTES",
  eosinophils: "EOSINOPHILS",
  basophils: "BASOPHILS",

  // Stress
  cortisol: "CORTISOL",
  dhea_s: "DHEA_S",
  dheas: "DHEA_S",
  dhea: "DHEA_S",
};

const TEMPLATE_BY_CODE: Record<string, MarkerTemplateItem> = MARKER_TEMPLATE.reduce((acc, t) => {
  acc[t.code] = t;
  return acc;
}, {} as Record<string, MarkerTemplateItem>);

const TEMPLATE_CODE_SET = new Set(MARKER_TEMPLATE.map((t) => t.code));

function buildTemplateRows(): MarkerState[] {
  return MARKER_TEMPLATE.map((t) => {
    const d = defaultRangeStrings(t.code);
    return {
      code: t.code,
      name: t.name,
      value: "",
      unit: t.unit ?? "",
      ref_low: d.ref_low,
      ref_high: d.ref_high,
      flag: "",
      notes: "",
    };
  });
}

function resolvedTemplateCode(init: MarkerState): string | null {
  const code = init.code?.trim() ?? "";
  const name = init.name?.trim() ?? "";

  // 1) Direct match to canonical template code.
  if (code && TEMPLATE_CODE_SET.has(code)) return code;

  // 2) Variant mapping from extractor ids.
  const codeVar = normVariant(code);
  if (codeVar && VARIANT_TO_TEMPLATE_CODE[codeVar]) return VARIANT_TO_TEMPLATE_CODE[codeVar];

  const nameVar = normVariant(name);
  if (nameVar && VARIANT_TO_TEMPLATE_CODE[nameVar]) return VARIANT_TO_TEMPLATE_CODE[nameVar];

  // 3) Last resort: try matching by normalized template name.
  if (name) {
    const nk = normKey(name);
    const hit = MARKER_TEMPLATE.find((t) => normKey(t.name) === nk);
    if (hit) return hit.code;
  }

  return null;
}

function mergeTemplateWithInitial(initialRows: MarkerState[]): MarkerState[] {
  const rows = buildTemplateRows();
  const byCode: Record<string, MarkerState> = rows.reduce((acc, r) => {
    acc[r.code] = r;
    return acc;
  }, {} as Record<string, MarkerState>);

  for (const init of initialRows) {
    const hasAnyEditable =
      !!(init.value ?? "").trim() ||
      !!(init.notes ?? "").trim() ||
      !!(init.flag ?? "").trim() ||
      !!(init.code ?? "").trim() ||
      !!(init.name ?? "").trim();

    if (!hasAnyEditable && !init.code?.trim() && !init.name?.trim()) continue;

    const tplCode = resolvedTemplateCode(init);
    if (!tplCode) continue;

    const base = byCode[tplCode];
    if (!base) continue;

    const nextRefLow = (init.ref_low ?? "").toString().trim();
    const nextRefHigh = (init.ref_high ?? "").toString().trim();

    byCode[tplCode] = {
      ...base,
      // Only copy editable fields from the initial data
      value: (init.value ?? "").toString(),
      notes: (init.notes ?? "").toString(),
      flag: (init.flag ?? "").toString(),
      // Preserve template defaults unless initial data provides a real value.
      ref_low: nextRefLow ? nextRefLow : base.ref_low,
      ref_high: nextRefHigh ? nextRefHigh : base.ref_high,
      // Preserve unit if backend returned a concrete unit (e.g. phosphate variants).
      unit: (init.unit ?? "").toString().trim() || base.unit,
    };
  }

  return rows.map((r) => byCode[r.code] ?? r);
}

function normalizeToIsoDate(input: string): { iso: string | null; error?: string } {
  const raw = (input ?? "").trim();
  if (!raw) return { iso: null };

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const y = Number(isoMatch[1]);
    const m = Number(isoMatch[2]);
    const d = Number(isoMatch[3]);
    if (m < 1 || m > 12) return { iso: null, error: "Month must be 1–12." };
    const dim = daysInMonth(y, m);
    if (d < 1 || d > dim) return { iso: null, error: "Invalid day for month." };
    return { iso: `${y}-${pad2(m)}-${pad2(d)}` };
  }

  const monthMap: Record<string, number> = {
    jan: 1, january: 1,
    feb: 2, february: 2,
    mar: 3, march: 3,
    apr: 4, april: 4,
    may: 5,
    jun: 6, june: 6,
    jul: 7, july: 7,
    aug: 8, august: 8,
    sep: 9, sept: 9, september: 9,
    oct: 10, october: 10,
    nov: 11, november: 11,
    dec: 12, december: 12,
  };

  const dMonY = raw.match(/^(\d{1,2})-([A-Za-z]{3,9})-(\d{4})$/);
  if (dMonY) {
    const d = Number(dMonY[1]);
    const monStr = dMonY[2].toLowerCase();
    const y = Number(dMonY[3]);
    const m = monthMap[monStr];
    if (!m) return { iso: null, error: "Unknown month name." };
    const dim = daysInMonth(y, m);
    if (d < 1 || d > dim) return { iso: null, error: "Invalid day for month." };
    return { iso: `${y}-${pad2(m)}-${pad2(d)}` };
  }

  const dMonSpaceY = raw.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
  if (dMonSpaceY) {
    const d = Number(dMonSpaceY[1]);
    const monStr = dMonSpaceY[2].toLowerCase();
    const y = Number(dMonSpaceY[3]);
    const m = monthMap[monStr];
    if (!m) return { iso: null, error: "Unknown month name." };
    const dim = daysInMonth(y, m);
    if (d < 1 || d > dim) return { iso: null, error: "Invalid day for month." };
    return { iso: `${y}-${pad2(m)}-${pad2(d)}` };
  }

  const dSep = raw.match(/^(\d{1,2})[\/.](\d{1,2})[\/.](\d{4})$/);
  if (dSep) {
    const d = Number(dSep[1]);
    const m = Number(dSep[2]);
    const y = Number(dSep[3]);
    if (m < 1 || m > 12) return { iso: null, error: "Month must be 1–12." };
    const dim = daysInMonth(y, m);
    if (d < 1 || d > dim) return { iso: null, error: "Invalid day for month." };
    return { iso: `${y}-${pad2(m)}-${pad2(d)}` };
  }

  return { iso: null, error: "Use YYYY-MM-DD or like 06-Sep-2025." };
}

function normalizeToIsoDatetime(input: string): { iso: string | null; error?: string } {
  const raw = (input ?? "").trim();
  if (!raw) return { iso: null };

  const isoDt = raw.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/);
  if (isoDt) return { iso: raw };

  const d = normalizeToIsoDate(raw);
  if (d.iso) return { iso: `${d.iso}T00:00:00Z` };
  if (d.error) return { iso: null, error: "Collected at must be ISO datetime or a date like 06-Sep-2025." };

  return { iso: null, error: "Collected at must be ISO datetime (e.g. 2025-09-06T00:00:00Z)." };
}

function coerceMarkersToArray(markers: any): MarkerState[] {
  if (!markers) return [];

  if (Array.isArray(markers)) {
    return markers.map((m: any) => ({
      code: typeof m?.code === "string" ? m.code : "",
      name: String(m?.name ?? m?.code ?? "").trim(),
      value: String(m?.value ?? "").trim(),
      unit: typeof m?.unit === "string" ? m.unit : "",
      ref_low: m?.ref_low != null ? String(m.ref_low) : "",
      ref_high: m?.ref_high != null ? String(m.ref_high) : "",
      flag: m?.flag != null ? String(m.flag) : "",
      notes: m?.notes != null ? String(m.notes) : (Array.isArray(m?.comments) ? m.comments.join("\n") : ""),
    }));
  }

  if (isPlainObject(markers)) {
    return Object.entries(markers).map(([code, v]: any) => ({
      code: String(code ?? ""),
      name: String(v?.name ?? code).trim(),
      value: String(v?.value ?? v?.raw_value ?? "").trim(),
      unit: typeof v?.unit === "string" ? v.unit : "",
      ref_low: v?.ref_low != null ? String(v.ref_low) : "",
      ref_high: v?.ref_high != null ? String(v.ref_high) : "",
      flag: v?.flag != null ? String(v.flag) : "",
      notes: v?.notes != null ? String(v.notes) : (Array.isArray(v?.comments) ? v.comments.join("\n") : ""),
    }));
  }

  return [];
}

function coercePatient(initial: any): PatientState {
  const p = isPlainObject(initial?.patient) ? initial.patient : (isPlainObject(initial) ? initial : {});
  const first = (p.first_name ?? p.given_name ?? p.patient_name ?? "").toString();
  const last = (p.last_name ?? p.family_name ?? "").toString();

  return {
    first_name: first,
    last_name: last,
    date_of_birth: (p.date_of_birth ?? p.dob ?? "").toString(),
    sex: (p.sex ?? "") as Sex,
    collected_at: (p.collected_at ?? p.sample_date ?? "").toString(),
    report_date: (p.report_date ?? "").toString(),
    lab_name: (p.lab_name ?? p.company ?? "").toString(),
    lab_reference: (p.lab_reference ?? p.report_number ?? "").toString(),
  };
}

/**
 * A marker is only "touched" when it would produce meaningful output.
 * Default ref ranges do NOT count as touched, so empty markers do not require values.
 */
function isMarkerTouched(m: MarkerState): boolean {
  return !!m.value.trim() || !!m.notes.trim() || !!m.flag.trim();
}

/**
 * Backend inference lookup is panel-aware. Some markers may be displayed in one UI grouping
 * while needing a different backend blueprint panel context.
 *
 * For now we patch only PHOSPHATE as requested.
 */
function getBackendMarkerContextForCode(code: string): {
  biomarker_id: string;
  panel_id: string;
  panel_title: string;
} | null {
  const c = String(code || "").trim().toUpperCase();

  if (c === "PHOSPHATE") {
    return {
      biomarker_id: "PHOSPHATE",
      panel_id: "bone",
      panel_title: "Bone",
    };
  }

  return null;
}

const ManualResultsForm: React.FC<ManualResultsFormProps> = ({
  initialData,
  initialTitle,
  initialSummary,
  initialGeneratePdf,
  submitLabel = "Save",
  onSubmit,
  onCancel,
}) => {
  const initialPatient = useMemo(() => coercePatient(initialData ?? {}), [initialData]);

  const initialMarkers = useMemo(() => {
    const maybe = isPlainObject(initialData) ? initialData.markers : undefined;
    const coerced = coerceMarkersToArray(maybe);
    return mergeTemplateWithInitial(coerced);
  }, [initialData]);

  const [title, setTitle] = useState<string>(initialTitle ?? "");
  const [summary, setSummary] = useState<string>(initialSummary ?? "");
  const [generatePdf, setGeneratePdf] = useState<boolean>(
    typeof initialGeneratePdf === "boolean" ? initialGeneratePdf : true
  );

  const [patient, setPatient] = useState<PatientState>(initialPatient);
  const [markers, setMarkers] = useState<MarkerState[]>(
    initialMarkers.length ? initialMarkers : buildTemplateRows()
  );

  const [notes, setNotes] = useState<string>(String((initialData?.notes ?? "") as any));

  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function clearMarker(idx: number) {
    setMarkers((m) =>
      m.map((row, i) => {
        if (i !== idx) return row;
        const d = defaultRangeStrings(row.code);
        return { ...row, value: "", ref_low: d.ref_low, ref_high: d.ref_high, notes: "", flag: "" };
      })
    );
  }

  function updateMarker(idx: number, patch: Partial<MarkerState>) {
    setMarkers((m) => m.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};

    const dobNorm = normalizeToIsoDate(patient.date_of_birth);
    if (patient.date_of_birth.trim() && dobNorm.error) {
      next["patient.date_of_birth"] = dobNorm.error;
    }

    const collectedNorm = normalizeToIsoDatetime(patient.collected_at);
    if (patient.collected_at.trim() && collectedNorm.error) {
      next["patient.collected_at"] = collectedNorm.error;
    }

    markers.forEach((m, idx) => {
      if (!isMarkerTouched(m)) return;
      if (!m.value.trim()) next[`markers.${idx}.value`] = "Required";
    });

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    setFormErr("");
    if (!validate()) return;

    const dobNorm = normalizeToIsoDate(patient.date_of_birth);
    const collectedNorm = normalizeToIsoDatetime(patient.collected_at);

    const submittedMarkers = markers
      .filter((m) => m.value.trim())
      .map((m) => {
        const backendCtx = getBackendMarkerContextForCode(m.code);

        return {
          code: m.code,
          name: m.name,
          value: m.value.trim(),
          unit: m.unit || null,
          ref_low: m.ref_low.trim() ? Number(m.ref_low) : null,
          ref_high: m.ref_high.trim() ? Number(m.ref_high) : null,
          flag: m.flag.trim() || null,
          notes: m.notes.trim() || null,
          ...(backendCtx
            ? {
                biomarker_id: backendCtx.biomarker_id,
                panel_id: backendCtx.panel_id,
                panel_title: backendCtx.panel_title,
              }
            : {}),
        };
      });

    const content_json: StructuredResult = {
      schema_version: "1.0",
      patient: {
        first_name: patient.first_name.trim() || null,
        last_name: patient.last_name.trim() || null,
        date_of_birth: dobNorm.iso,
        sex: patient.sex || null,
        collected_at: collectedNorm.iso,
        report_date: patient.report_date.trim() || null,
        lab_name: patient.lab_name.trim() || null,
        lab_reference: patient.lab_reference.trim() || null,
      },
      markers: submittedMarkers,
      notes: notes.trim() || null,
    };

    const payload: AdminManualResultIn = {
      title: title.trim() || null,
      summary: summary.trim() || null,
      generate_pdf: generatePdf,
      content_json,
    };

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (e: any) {
      setFormErr(getErrorMessage(e, "Failed to save result"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {formErr && (
        <div className="border border-red-300 bg-red-50 text-red-700 p-2 rounded">
          {formErr}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <Input id="result-title" label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input id="result-summary" label="Summary (optional)" value={summary} onChange={(e) => setSummary(e.target.value)} />

        <Input
          id="patient-first"
          label="First name"
          value={patient.first_name}
          onChange={(e) => setPatient((p) => ({ ...p, first_name: e.target.value }))}
        />
        <Input
          id="patient-last"
          label="Last name"
          value={patient.last_name}
          onChange={(e) => setPatient((p) => ({ ...p, last_name: e.target.value }))}
        />

        <Input
          id="patient-dob"
          label="Date of birth (YYYY-MM-DD)"
          value={patient.date_of_birth}
          onChange={(e) => setPatient((p) => ({ ...p, date_of_birth: e.target.value }))}
          error={fieldErrors["patient.date_of_birth"]}
          placeholder="e.g. 1986-02-17 or 17-Feb-1986"
        />

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Sex</label>
          <select
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
            value={patient.sex}
            onChange={(e) => setPatient((p) => ({ ...p, sex: e.target.value as Sex }))}
          >
            <option value="">—</option>
            <option value="Female">Female</option>
            <option value="Male">Male</option>
            <option value="Other">Other</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>

        <Input
          id="patient-collected"
          label="Collected at (ISO datetime or date)"
          value={patient.collected_at}
          onChange={(e) => setPatient((p) => ({ ...p, collected_at: e.target.value }))}
          error={fieldErrors["patient.collected_at"]}
          placeholder="e.g. 2025-09-06T00:00:00Z or 06-Sep-2025"
        />

        <Input
          id="patient-report-date"
          label="Report date (optional)"
          value={patient.report_date}
          onChange={(e) => setPatient((p) => ({ ...p, report_date: e.target.value }))}
        />

        <Input
          id="patient-lab"
          label="Lab name (optional)"
          value={patient.lab_name}
          onChange={(e) => setPatient((p) => ({ ...p, lab_name: e.target.value }))}
        />

        <Input
          id="patient-lab-ref"
          label="Lab reference (optional)"
          value={patient.lab_reference}
          onChange={(e) => setPatient((p) => ({ ...p, lab_reference: e.target.value }))}
        />
      </div>

      <div className="border rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold">Markers</div>
        </div>

        <div className="space-y-2">
          {markers.map((m, idx) => (
            <MarkerRowEditor
              key={m.code || idx}
              marker={m}
              onChange={(patch) => updateMarker(idx, patch)}
              onRemove={() => clearMarker(idx)}
              errors={{
                value: fieldErrors[`markers.${idx}.value`],
              }}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Notes (optional)</label>
        <textarea
          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={generatePdf} onChange={(e) => setGeneratePdf(e.target.checked)} />
          Generate branded PDF
        </label>
      </div>

      <div className="flex gap-2">
        <Button type="button" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving…" : submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export default ManualResultsForm;